import React, {
  useContext,
  useLayoutEffect,
  useState,
  useEffect,
  useCallback
} from 'react';
import {
  AppBar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { debounce } from 'lodash';

import { spacingInRems } from '../../theme/themeUtils';
import useResizeObserver from '../../hooks/useResizeObserver';
import PaymentItem from '../routes/bills/Menu/PaymentItem/PaymentItem';
import OfferItem from '../routes/bills/Menu/OfferItem/OfferItem';
import PaymentBalanceInfo from '../routes/bills/Menu/PaymentBalanceInfo';
import { storeContext } from '../../context';
import Backdrop from '../general/Backdrop';
import { t } from '../../../utils/i18next';
import { Alert } from '@mui/material';

const StyledAppBar = styled(AppBar)`
  && {
    position: fixed;
    bottom: 0;
    top: auto;
    width: 100%;
    padding: ${spacingInRems(1)} 0;
    box-shadow: 0 2px 2px 0 rgb(0 0 0 / 24%), 0 0 2px 0 rgb(0 0 0 / 12%);
    .bg {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
      background-color: #fff;
    }
    .alertStatus {
      z-index: 1;
      width: calc(100% - 2rem);
      max-width: 35rem;
      align-self: center;
      margin-bottom: 1.5rem;
      white-space: break-spaces;
      align-items: center;
    }
    .paymentItem {
      position: relative;
      display: flex;
      justify-content: center;
      cursor: default;
      align-self: center;
      max-width: 35rem;
      min-width: 35rem;
      h6:not(.selectBillTotal) {
        font-size: 1.4rem;
        line-height: 1rem;
      }
      p.amount {
        line-height: 1.8rem;
        span {
          font-size: 1.8rem;
        }
      }
      .buttonContainer {
        min-width: 25rem;
        button {
          margin-top: 1rem;
        }
      }
    }
    .expansionPanel {
      &:before {
        content: none;
      }
      box-shadow: none;
      max-width: 35rem;
      min-width: 35rem;
      align-self: center;
      .expansionPanelSummary {
        display: flex;
        justify-content: center;
        margin: 0;
        .summaryContent {
          margin: 0;
        }
        .expandIcon {
          position: absolute;
          top: 0;
          right: 0;
          & > * {
            pointer-events: none;
          }
        }

        .paymentItem {
          max-width: unset;
          width: 100%;
          .buttonContainer {
            button {
              .MuiButton-label {
                //block nested children's mouse events to allow accurate selection in onBackdropClick() function
                pointer-events: none;
              }
            }
          }
        }
      }
      .expansionPanelDetails {
        justify-content: center;
        > div {
          width: 100%;
        }
      }
    }
    &&.footerEmbedded {
      position: sticky;
    }
  }
`;
const debouncedFunc = debounce(func => func(), 150);

const Footer = observer(() => {
  const {
    payable,
    translations: { currentLanguage },
    userState,
    embeddedModel: { isEmbeddedPage },
    router: { currentPage }
  } = useContext(storeContext);

  const {
    hasPaymentPlan,
    hasActivePaymentPlan,
    paymentPlan,
    standingAmount,
    onlyBillId
  } = payable || {};

  const [isExpanded, setIsExpanded] = useState(false);
  const [ref, { contentRect }] = useResizeObserver();
  const { pathname } = currentPage;
  const { paymentItemPopoverIsOpen } = userState;

  const matches = useMediaQuery(theme => theme.breakpoints.down('lg'));
  const shouldShow = useCallback(() => {
    //only show payment footer on '/bills' route and if standingAmount > 0
    if (!pathname || !matches) return false;
    return (
      (onlyBillId && pathname.includes('/bills/')) ||
      (pathname === '/bills' && standingAmount > 0)
    );
  }, [pathname, matches]);

  useLayoutEffect(() => {
    if (contentRect && shouldShow()) {
      debouncedFunc(() => userState.set('footerHeight', contentRect.height));
    }
  }, [contentRect]);
  useEffect(() => {
    !shouldShow() && debouncedFunc(() => userState.set('footerHeight', 0));
  }, [shouldShow()]);

  const { MAKE_A_PAYMENT, PAYMENT_PLAN, VIEW_PAYMENT_PLAN_OPTIONS } =
    PaymentItem.paymentTypes;

  const { ANCHOR_TO_TOP } = PaymentItem.popoverTypes;

  const getPaymentType = () => {
    if (hasPaymentPlan) {
      return PAYMENT_PLAN;
    } else {
      return payable?.displayOfferInBillsPage
        ? VIEW_PAYMENT_PLAN_OPTIONS
        : MAKE_A_PAYMENT;
    }
  };

  const onExpansionPanelClick = e => {
    try {
      if (
        e.target &&
        e.target.parentNode.className.includes('expandIconContainer')
      ) {
        setIsExpanded(!isExpanded);
      }
    } catch (error) {
      console.warn(e.target);
      console.error(error);
    }
  };

  const onBackdropClick = e => {
    try {
      if (
        e.target &&
        e.target.className &&
        e.target.className.includes &&
        e.target.className.includes('backdrop')
      ) {
        setIsExpanded(false);
      }
    } catch (error) {
      console.warn(e.target);
      console.warn(e.target.className);
      console.error(error);
    }
  };

  const getAlert = () => {
    return (
      hasPaymentPlan &&
      !hasActivePaymentPlan && (
        <Alert className="alertStatus" severity="info" color="warning">
          {t(`payables.payment_options.message.${paymentPlan.status}`)}
        </Alert>
      )
    );
  };

  const getPaymentItem = () =>
    hasActivePaymentPlan ? (
      <Accordion
        className={'expansionPanel'}
        expanded={isExpanded}
        onClick={onExpansionPanelClick}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          classes={{
            content: 'summaryContent',
            expandIcon: 'expandIcon'
          }}
          className={'expansionPanelSummary expandIconContainer'}
          aria-controls="footer-panel-content"
          id="footer-panel-header"
        >
          <PaymentItem
            type={getPaymentType()}
            popoverType={ANCHOR_TO_TOP}
            buttonColor={'primary'}
          />
        </AccordionSummary>
        <AccordionDetails className={'expansionPanelDetails'}>
          <PaymentBalanceInfo />
        </AccordionDetails>
      </Accordion>
    ) : (
      <>
        {getAlert()}
        <PaymentItem
          type={getPaymentType()}
          popoverType={ANCHOR_TO_TOP}
          buttonColor={'primary'}
        />
      </>
    );

  const className = `footer${isEmbeddedPage ? 'Embedded' : ''}`;
  const testId = `footer${isEmbeddedPage ? '-embedded' : ''}`;

  return (
    <>
      {!shouldShow() ? null : (
        <StyledAppBar className={className} data-testid={testId}>
          <Backdrop
            show={isExpanded || paymentItemPopoverIsOpen}
            onClick={onBackdropClick}
          />
          <div className={'bg'} ref={ref} />
          {payable?.displayOfferInBillsPage ? (
            <OfferItem isFooterDisplay={true} />
          ) : (
            getPaymentItem()
          )}
        </StyledAppBar>
      )}
    </>
  );
});

export default Footer;
